import React, { useState } from 'react';
import { Layout as AntLayout, Menu, Button, Space, Avatar, Dropdown } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  SettingOutlined,
  CodeOutlined,
  ExperimentOutlined,
  UserOutlined,
  LogoutOutlined
} from '@ant-design/icons';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../contexts/ThemeContext';

const { Header, Sider, Content } = AntLayout;

interface FallbackLayoutProps {
  children: React.ReactNode;
}

const FallbackLayout: React.FC<FallbackLayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { themeConfig } = useTheme();

  const menuItems = [
    {
      key: '/fallbackapp/dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
    {
      key: '/fallbackapp/system-workflows',
      icon: <ExperimentOutlined />,
      label: 'System Workflows',
    },
    {
      key: '/fallbackapp/api-playground',
      icon: <CodeOutlined />,
      label: 'API Playground',
    },
    {
      key: '/fallbackapp/settings',
      icon: <SettingOutlined />,
      label: 'Settings',
    },
  ];

  const handleMenuClick = (key: string) => {
    navigate(key);
  };

  const userMenu = (
    <Menu
      items={[
        {
          key: 'profile',
          icon: <UserOutlined />,
          label: 'Profile',
        },
        {
          key: 'logout',
          icon: <LogoutOutlined />,
          label: 'Logout',
          onClick: logout,
        },
      ]}
    />
  );

  return (
    <AntLayout style={{ minHeight: '100vh' }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        style={{
          background: themeConfig.primaryColor, // Use theme primary color
          boxShadow: '2px 0 6px rgba(0, 21, 41, 0.1)',
        }}
        width={250}
      >
        <div
          style={{
            height: '64px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            padding: collapsed ? '0' : '0 24px',
            background: 'rgba(255, 255, 255, 0.1)',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '16px',
          }}
        >
          {collapsed ? (
            <ExperimentOutlined style={{ fontSize: '24px' }} />
          ) : (
            'Experimental'
          )}
        </div>
        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          style={{
            background: 'transparent',
            border: 'none',
          }}
          theme="dark"
          items={menuItems.map(item => ({
            ...item,
            onClick: () => handleMenuClick(item.key),
            style: { color: 'white' },
          }))}
        />
      </Sider>

      <AntLayout>
        <Header
          style={{
            padding: '0 16px',
            background: '#fff',
            boxShadow: '0 1px 4px rgba(0, 21, 41, 0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{
              fontSize: '16px',
              width: 64,
              height: 64,
            }}
          />

          <Space>
            <span>Experimental App</span>
            <Dropdown overlay={userMenu} placement="bottomRight">
              <Space style={{ cursor: 'pointer' }}>
                <Avatar size="small" icon={<UserOutlined />} />
                <span>{user?.firstName ? `${user.firstName} ${user.lastName}` : user?.email}</span>
              </Space>
            </Dropdown>
          </Space>
        </Header>

        <Content
          style={{
            margin: '24px',
            padding: '24px',
            background: '#fff',
            borderRadius: '8px',
            minHeight: 'calc(100vh - 112px)',
          }}
        >
          {children}
        </Content>
      </AntLayout>
    </AntLayout>
  );
};

export default FallbackLayout;